import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import type { LifecycleStateLabel, ProjectRun } from "@/api/projects";

// Tenant-wide runs feed. Same row shape as the project-scoped endpoint
// but with ``project_slug`` populated on every row so the global table
// can render a Project column.
//
// Backend endpoint lands in PR-07 (#144); until then the route 404s
// against the dev server and tests stub fetch.

/** A flow run's own latched status (`flow_runs.status`) — NOT a lifecycle
 * label. Mirrors `FlowRunStatusLabel` in `lifecycle/flow_run.py`. */
export type FlowRunStatus = "running" | "succeeded" | "failed";

/** The step a flow run failed at (flowstack#1799). `run_id` is null when the
 * step never got a run (refused before dispatch). */
export interface FlowRunFailingStep {
  node_name: string | null;
  run_id: string | null;
  status: LifecycleStateLabel | null;
}

/**
 * One flow submission rolled up for the Runs tab (flowstack#1799). Mirrors
 * `FlowRunFeedSummary` in `server/routes/projects.py`.
 *
 * The counts cover EVERY step of the flow run, including steps older than the
 * feed's row window, so `steps_total` can exceed the rows the feed returned
 * for it. A step is one run: a fanned node counts once per child. While the
 * flow run is `running`, `steps_total` is the steps dispatched so far.
 */
export interface FlowRunFeedSummary {
  flow_run_id: string;
  flow_name: string | null;
  /** null when the server has no flow_runs row for the id (a pre-#371 child). */
  status: FlowRunStatus | null;
  steps_total: number;
  steps_settled: number;
  steps_failed: number;
  submitted_at: string;
  finished_at: string | null;
  failing_step: FlowRunFailingStep | null;
  /** A user cancelled it (fs#2567): `status` is `failed` and `failing_step`
   * null. Absent from an older server. */
  cancelled?: boolean;
}

export interface RunListResponse {
  runs: ProjectRun[];
  /** Additive (flowstack#1799): absent from an older server. */
  flow_runs?: FlowRunFeedSummary[];
}

export const RUNS_QUERY_KEY = ["runs", "list"] as const;

export function useRuns() {
  return useQuery({
    queryKey: RUNS_QUERY_KEY,
    queryFn: async (): Promise<ProjectRun[]> => {
      const response = await apiFetch<RunListResponse>("/api/runs");
      return response.runs;
    },
    // Runs change often; short stale matches the project-scoped feed.
    staleTime: 10_000,
  });
}

/**
 * The same `/api/runs` feed with its flow-run rollups, for the Runs tab.
 *
 * A separate key from {@link RUNS_QUERY_KEY} because that key's cache holds
 * the bare row ARRAY that {@link useRuns} (the Dashboard) reads. It sits UNDER
 * that key, so every `invalidateQueries({ queryKey: RUNS_QUERY_KEY })` after a
 * submit refreshes this one too; and each fetch writes the rows back under
 * `RUNS_QUERY_KEY`, so the Dashboard opens warm after the Runs tab. The
 * sidebar does NOT count these rows: they are one capped page, not a total
 * (flowstack#1866).
 */
export const RUN_FEED_QUERY_KEY = [...RUNS_QUERY_KEY, "with-flow-runs"] as const;

export interface RunFeed {
  runs: ProjectRun[];
  flowRuns: FlowRunFeedSummary[];
}

export function useRunFeed() {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: RUN_FEED_QUERY_KEY,
    queryFn: async (): Promise<RunFeed> => {
      const response = await apiFetch<RunListResponse>("/api/runs");
      queryClient.setQueryData(RUNS_QUERY_KEY, response.runs);
      return { runs: response.runs, flowRuns: response.flow_runs ?? [] };
    },
    staleTime: 10_000,
  });
}

/**
 * How many of the tenant's runs completed and failed in the trailing window
 * (flowstack#1983). Mirrors `RunCountsResponse` in `server/routes/runs.py`.
 *
 * Counted SERVER-side, by when each run settled: `/api/runs` is one capped
 * page, so a count over it would be silently low on a busy tenant.
 * `failed` is the four `failed_*` classes together.
 */
export interface RunCounts {
  window_days: number;
  since: string;
  as_of: string;
  completed: number;
  failed: number;
}

export const RUN_COUNTS_QUERY_KEY = ["runs", "counts"] as const;

export function useRunCounts() {
  return useQuery({
    queryKey: RUN_COUNTS_QUERY_KEY,
    queryFn: () => apiFetch<RunCounts>("/api/run-counts"),
    staleTime: 30_000,
  });
}

// Single-run detail. ``Run`` extends ProjectRun with the per-Tile
// invocation rollup the detail page shows. Same wire shape as the
// row in the runs list, plus the dispatched-tile invocations that
// the Flow runner emits at compile time (one row per fanned child;
// for a Tile run, exactly one row).
export interface RunInvocation {
  invocation_id: string;
  tile_name: string;
  node_name: string | null; // null for single-Tile (non-Flow) runs
  status: ProjectRun["status"];
  cache_hit: boolean;
  submitted_at: string;
  // flowstack#1270 / #961 — the run's window, from the one source
  // `timing_source` names, and the duration DERIVED from it server-side
  // (`finished - started`, clamped at zero; never stored twice). The duration
  // is null unless both ends exist, and null means *not recorded*: see the
  // same fields on `ProjectRun` for why that is never a statement about the run.
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  /** Additive (flowstack#961): absent from an older server. */
  timing_source?: ProjectRun["timing_source"];
}

export interface Run extends ProjectRun {
  invocations: RunInvocation[];
}

export function runQueryKey(runId: string): readonly unknown[] {
  return ["runs", "detail", runId] as const;
}

/** Lifecycle states the runner is actively progressing through.
 * Mirrors flowstack.lifecycle.state_machine; used to gate polling
 * (per Rupert PR-259 r1: terminal runs must stop polling). */
export const ACTIVE_RUN_STATES: ReadonlySet<LifecycleStateLabel> = new Set([
  "validating",
  "cached",
  "staging_inputs",
  "dispatched",
  "running",
  "collecting_outputs",
  "validating_outputs",
]);

/**
 * Lifecycle states a run can carry an ANALYSIS in.
 *
 * `completed`, plus `failed_scientific` (Rupert PR-935 r0): the materialize
 * route does not gate on lifecycle status at all — it walks whatever
 * `TorsionRefinementResult`/`TorsionScanResult` artifacts the run published —
 * and a run whose SCIENCE was rejected is often the one most worth inspecting.
 * `failed_validation` (nothing ever ran) and `failed_system`/`failed_contract`
 * (infra, not science) stay out: an analysis needs finished science to fit
 * against.
 *
 * Lives here, beside `ACTIVE_RUN_STATES`, because BOTH analysis surfaces read
 * it — the per-project run list and the cross-run comparison's run picker
 * (VIZ U2.1). Two copies of this predicate would let the two lists disagree
 * about which runs are comparable.
 */
export const ANALYZABLE_RUN_STATES: ReadonlySet<LifecycleStateLabel> = new Set([
  "completed",
  "failed_scientific",
]);

export function useRun(runId: string) {
  return useQuery({
    queryKey: runQueryKey(runId),
    queryFn: async (): Promise<Run> => {
      return apiFetch<Run>(`/api/runs/${encodeURIComponent(runId)}`);
    },
    staleTime: 10_000,
    enabled: Boolean(runId),
    // Per Rupert PR-259 r1 (blocker): the docstring promised 5s
    // polling while a run is active but neither useRun nor the run
    // detail page actually wired refetchInterval. Pick 5s while the
    // run is in any active lifecycle state; once terminal, stop —
    // a refetch on a completed/failed run never produces a state
    // change.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      return ACTIVE_RUN_STATES.has(status) ? 5_000 : false;
    },
  });
}

/**
 * Whether this run was SERVED from the cache rather than executed.
 *
 * Reads the server's `cache_hit` (the durable `cached` transition, #1329) off
 * the run's own one-row rollup, never a null `started_at`: absence of timing is
 * not evidence of caching (see `components/runs/run-timing.ts`). Every row must
 * say so, so an empty rollup is not a hit.
 */
export function runServedFromCache(run: Pick<Run, "invocations">): boolean {
  return run.invocations.length > 0 && run.invocations.every((inv) => inv.cache_hit);
}

/** One `run_outputs` row on the provenance bundle. Mirrors `RunOutputArtifact`
 * in `server/routes/runs.py`. */
export interface RunOutputArtifact {
  port: string;
  ordinal: number;
  content_hash: string;
}

export type Attestation = "reported" | "mismatch" | "unattested";

/** `GET /api/runs/{id}/provenance`. Mirrors `RunProvenance` in
 * `server/routes/runs.py`. */
export interface RunProvenanceBody {
  manifest_name: string | null;
  manifest_version: string | null;
  container_digest: string | null;
  parameter_hash: string | null;
  cache_key: string | null;
  parameter_values: Record<string, unknown> | null;
  git_sha: string | null;
  input_artifact_hashes: string[];
  output_artifacts: RunOutputArtifact[];
  container_digest_actual: string | null;
  provenance_source: string | null;
  attestation: Attestation;
}

/**
 * The run's provenance bundle. One query key for the Provenance tab and the
 * Outputs tab (a cache hit's outputs are its `output_artifacts`, flowstack#1942),
 * so opening both fetches once.
 *
 * `isActive` polls while the run is in flight: outputs and git SHA land only
 * when the run produces artifacts (mirrors RunLogs' 5s active cadence).
 */
export function useRunProvenance(runId: string, isActive = false) {
  return useQuery<RunProvenanceBody>({
    queryKey: ["runs", "provenance", runId],
    queryFn: async () =>
      apiFetch<RunProvenanceBody>(`/api/runs/${encodeURIComponent(runId)}/provenance`),
    staleTime: 10_000,
    refetchInterval: isActive ? 5_000 : false,
    // The Tile detail sheet asks for a node's run that may not exist yet.
    enabled: Boolean(runId),
  });
}
